import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import SignaturePad from 'react-signature-canvas';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const QuitusForm = forwardRef(({ declaration }, ref) => {
    const today = new Date().toISOString().split('T')[0];
    const [formData, setFormData] = useState({
        entreprise: '',
        travaux: '',
        maitreOuvrage: '',
        client: '',
        clientEmail: '',
        dateReception: today,
        dateFait: today,
        decision: 'acceptée',
        delai: '',
        ville: '',
    });

    console.log('Declaration in QuitusForm:', declaration);

    const [reserves, setReserves] = useState([{ text: '', files: [] }]);
    const signatureClientRef = useRef();
    const signatureEntrepriseRef = useRef();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleReserveChange = (index, value) => {
        const updated = [...reserves];
        updated[index].text = value;
        setReserves(updated);
    };

    const handleFileUpload = (index, files) => {
        const updated = [...reserves];
        updated[index].files = Array.from(files);
        setReserves(updated);
    };

    const addReserve = () => setReserves([...reserves, { text: '', files: [] }]);
    const removeReserve = (index) => setReserves(reserves.filter((_, i) => i !== index));

    const fileToDataUrl = (file) =>
        new Promise((resolve) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.readAsDataURL(file);
        });

    const formatFR = (dateStr) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };


    const buildDocDefinition = async () => {
        const signatureClient = signatureClientRef.current?.getCanvas().toDataURL('image/png') || '';
        const signatureEntreprise = signatureEntrepriseRef.current?.getCanvas().toDataURL('image/png') || '';

        // Couleurs & branding
        const primary = '#ea580c';
        const muted = '#6b7280';
        const lightGray = '#f3f4f6';

        const decisionText = {
            acceptée: `La réception des travaux est prononcée sans réserves avec effet à la date du ${formatFR(formData.dateReception)}.`,
            réservée: `La réception des travaux est prononcée avec les réserves consignées dans l’annexe ci-jointe à la date du ${formatFR(formData.dateReception)}.`,
            refusée: `La réception des travaux est refusée pour les motifs consignés dans l’annexe ci-jointe à la date du ${formatFR(formData.dateReception)}.`,
        };

        const chantierTable = {
            layout: {
                hLineColor: () => '#e5e7eb',
                vLineColor: () => '#e5e7eb',
                paddingLeft: () => 8,
                paddingRight: () => 8,
                paddingTop: () => 6,
                paddingBottom: () => 6,
                fillColor: (rowIndex) => (rowIndex % 2 ? null : lightGray),
            },
            table: {
                widths: ['30%', '70%'],
                body: [
                    [{ text: 'Entreprise', style: 'kvKey' }, { text: formData.entreprise || '—', style: 'kvValue' }],
                    [{ text: 'Adresse', style: 'kvKey' }, { text: `${declaration.Adresse || ''} ${declaration.Ville || ''} ${declaration.Pays || ''}`.trim() || '—', style: 'kvValue' }],
                    [{ text: 'Travaux', style: 'kvKey' }, { text: formData.travaux || '—', style: 'kvValue' }],
                    [{ text: "Maître d’ouvrage", style: 'kvKey' }, { text: formData.maitreOuvrage || '—', style: 'kvValue' }],
                    [{ text: 'Client', style: 'kvKey' }, { text: formData.client || '—', style: 'kvValue' }],
                    [{ text: 'Date de réception', style: 'kvKey' }, { text: formatFR(formData.dateReception) || '—', style: 'kvValue' }],
                ]
            }
        };

        const content = [
            {
                margin: [0, 0, 0, 12],
                canvas: [{ type: 'rect', x: 0, y: 0, w: 515, h: 36, r: 6, color: primary, lineColor: primary }]
            },
            {
                text: 'PROCÈS-VERBAL DE RÉCEPTION DE TRAVAUX',
                absolutePosition: { x: 0, y: 10 },
                alignment: 'center',
                color: '#fff',
                fontSize: 14,
                bold: true
            },

            { text: 'Informations générales', style: 'h2', margin: [0, 24, 0, 8] },
            chantierTable,

            { text: 'Décision', style: 'h2', margin: [0, 18, 0, 4] },
            { text: decisionText[formData.decision], margin: [0, 4, 0, 0] },

            { text: decisionText[formData.decision] !== 'acceptée' ? `L'entreprise et le Client conviennent que les travaux nécessités par les réserves ci-dessous seront exécutés dans un délai de ${formData.delai || '—'} jours.` : '', margin: [0, 6, 0, 0] },

            { text: 'Formule', style: 'h2', margin: [0, 18, 0, 4] },
            {
                columns: [
                    { width: '*', text: `Fait à ${formData.ville || '—'}, le ${formatFR(formData.dateFait) || '—'}` },
                    {
                        width: 120,
                        qr: `${declaration?.UUID_ || ''}|${formatFR(formData.dateReception) || ''}|${formData.decision || ''}`,
                        fit: 90
                    }
                ],
                columnGap: 12
            },

            { text: 'Signatures', style: 'h2', margin: [0, 22, 0, 10] },
            {
                columns: [
                    {
                        width: '*',
                        stack: [
                            { text: "Signature de l'entreprise", style: 'sigLabel' },
                            {
                                margin: [0, 6, 16, 0],
                                table: {
                                    widths: ['*'],
                                    body: [[
                                        {
                                            image: signatureEntreprise || undefined,
                                            fit: [240, 80],
                                            alignment: 'left',
                                            margin: [6, 6, 6, 6]
                                        }
                                    ]]
                                },
                                layout: {
                                    hLineColor: '#d1d5db',
                                    vLineColor: '#d1d5db',
                                    paddingLeft: () => 0,
                                    paddingRight: () => 0,
                                    paddingTop: () => 0,
                                    paddingBottom: () => 0
                                }
                            }
                        ]
                    },
                    {
                        width: '*',
                        stack: [
                            { text: 'Signature du client', style: 'sigLabel' },
                            {
                                margin: [0, 6, 0, 0],
                                table: {
                                    widths: ['*'],
                                    body: [[
                                        {
                                            image: signatureClient || undefined,
                                            fit: [240, 80],
                                            alignment: 'left',
                                            margin: [6, 6, 6, 6]
                                        }
                                    ]]
                                },
                                layout: {
                                    hLineColor: '#d1d5db',
                                    vLineColor: '#d1d5db',
                                    paddingLeft: () => 0,
                                    paddingRight: () => 0,
                                    paddingTop: () => 0,
                                    paddingBottom: () => 0
                                }
                            }
                        ]
                    }
                ],
                columnGap: 18
            }
        ];

        if (formData.decision !== 'acceptée') {
            content.push(
                { text: '', pageBreak: 'before' },
                { text: 'ANNEXE — LISTE DES RÉSERVES', style: 'h1' },
                { text: 'Les réserves ci-dessous sont à lever dans les délais convenus.', color: muted, margin: [0, 2, 0, 10] }
            );

            const ol = reserves.map((r, idx) => ({
                text: r.text?.trim() || `(Réserve ${idx + 1} non renseignée)`,
                margin: [0, 2, 0, 2]
            }));
            content.push({ ol, margin: [0, 4, 0, 12] });

            for (let i = 0; i < reserves.length; i++) {
                const res = reserves[i];
                if (!res.files || res.files.length === 0) continue;

                content.push(
                    { text: `Réserve ${i + 1} — Fichiers joints`, style: 'h2', margin: [0, 6, 0, 6] }
                );

                const items = [];
                const imagePreviews = [];

                for (const f of res.files) {
                    const dataUrl = await fileToDataUrl(f);

                    items.push({
                        columns: [
                            { text: f.type.includes('pdf') ? '📄' : (f.type.startsWith('image/') ? '🖼️' : '📎'), width: 18 },
                            { text: f.name, link: dataUrl, color: primary, decoration: 'underline', width: '*', margin: [2, 0, 0, 0] },
                            { text: (f.size ? `(${(f.size / 1024).toFixed(1)} Ko)` : ''), color: muted, width: 'auto' }
                        ],
                        columnGap: 6,
                        margin: [0, 2, 0, 2]
                    });

                    if (f.type.startsWith('image/')) {
                        imagePreviews.push({
                            image: dataUrl,
                            width: 220,
                            margin: [0, 6, 10, 6]
                        });
                    }
                }

                content.push({ stack: items, margin: [0, 0, 0, imagePreviews.length ? 6 : 10] });

                if (imagePreviews.length) {
                    content.push({
                        columns: imagePreviews.map((img) => ({ ...img, alignment: 'left' })),
                        columnGap: 10,
                        margin: [0, 0, 0, 6]
                    });
                }
            }
        }

        const header = (currentPage, pageCount, pageSize) => {
            return {
                margin: [40, 20, 40, 0],
                columns: [
                    {
                        width: '*',
                        text: (declaration?.Nom || 'Quitus de réception'),
                        color: muted,
                        fontSize: 9
                    },
                    {
                        width: 'auto',
                        text: `Ref: ${declaration?.UUID_ || '—'}`,
                        color: muted,
                        fontSize: 9
                    }
                ]
            };
        };

        const footer = (currentPage, pageCount) => {
            return {
                margin: [40, 0, 40, 20],
                columns: [
                    { text: `Généré le ${new Date().toLocaleString('fr-FR')}`, color: muted, fontSize: 9 },
                    { text: `Page ${currentPage} / ${pageCount}`, alignment: 'right', color: muted, fontSize: 9 }
                ]
            };
        };

        // Filigrane s’il y a réserves / refus
        const watermark =
            formData.decision === 'refusée'
                ? { text: 'REFUSÉ', color: '#ef4444', opacity: 0.08, bold: true, italics: true }
                : formData.decision === 'réservée'
                    ? { text: 'RÉSERVES', color: primary, opacity: 0.06, bold: true, italics: true }
                    : null;

        return {
            info: {
                title: `Quitus_${(declaration?.Nom || 'document').replace(/\s+/g, '_')}.pdf`,
                author: formData.entreprise || '—',
                subject: 'Procès-verbal de réception de travaux',
                keywords: 'quitus, réception, travaux',
            },
            header,
            footer,
            watermark,
            content,
            pageSize: 'A4',
            pageMargins: [40, 80, 40, 60],
            defaultStyle: { fontSize: 10, color: '#111827' }, // gray-900
            styles: {
                h1: { fontSize: 18, bold: true, color: '#111827', margin: [0, 0, 0, 6] },
                h2: { fontSize: 12, bold: true, color: '#111827' },
                kvKey: { bold: true, color: '#111827' },
                kvValue: {},
                sigLabel: { fontSize: 10, bold: true, color: '#111827' },
            }
        };
    };

    // Expose au parent : getPdfBase64() + getTitle() + isValid()
    useImperativeHandle(ref, () => ({
        getPdfBase64: async () =>
            new Promise(async (resolve) => {
                const def = await buildDocDefinition();
                pdfMake.createPdf(def).getBase64((b64) => resolve(b64));
            }),
        getTitle: () => `Quitus_${(declaration?.Nom || 'document').replace(/\s+/g, '_')}`,
        isValid: () => !!(formData.entreprise && formData.client && formData.dateReception)
    }));

    return (
        <div className="bg-gray-200 py-10">
            <div className="relative bg-white shadow-md mx-auto px-20 py-24 w-full max-w-[1000px]">
                {/* <button
                    onClick={async () => {
                        const def = await buildDocDefinition();
                        pdfMake.createPdf(def).open();
                    }}
                    className="top-0 left-0 fixed bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded text-white"
                >
                    <i class="fa-solid fa-eye"></i> Générer le PDF (aperçu)
                </button> */}
                <h2 className="mb-8 font-semibold text-2xl text-center">Procès-verbal de réception de travaux</h2>
                <div className="my-10 border-t"></div>

                <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                        <span className="w-36 text-gray-600">Entreprise :</span>
                        <input name="entreprise" placeholder="Société" value={formData.entreprise} onChange={handleChange} className="p-2 border rounded w-64" />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-36 text-gray-600">{declaration.Pays}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-36 text-gray-600">Travaux :</span>
                        <input name="travaux" placeholder="Description des travaux" value={formData.travaux} onChange={handleChange} className="p-2 border rounded w-64" />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-36 text-gray-600">{declaration.Adresse} {declaration.Ville}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-36 text-gray-600">Maître d’ouvrage :</span>
                        <input name="maitreOuvrage" value={formData.maitreOuvrage} onChange={handleChange} className="p-2 border rounded w-64" />
                    </div>
                </div>

                <div className="my-10 border-t"></div>

                <div className="mt-6 text-gray-800 text-sm">
                    <p>
                        Je soussigné <input name="client" placeholder="Nom du client" value={formData.client} onChange={handleChange} className="flex-1 p-2 border rounded" /> {" "}
                        (<input
                            type="email"
                            name="clientEmail"
                            value={formData.clientEmail}
                            onChange={handleChange}
                            placeholder="Email du client"
                            className="ml-2 p-2 border rounded w-64" />), client, déclare avoir procédé le <input type="date" name="dateReception" value={formData.dateReception} onChange={handleChange} className="p-2 border rounded" /> à l'examen des travaux exécutés.
                    </p>
                    <p className="mt-2">Pour les travaux réalisés par l’entreprise ci-dessus, je déclare :</p>
                </div>

                <div className="flex flex-col gap-4 mt-4 text-sm">
                    <label>
                        <input type="radio" name="decision" value="acceptée" checked={formData.decision === 'acceptée'} onChange={handleChange} />
                        <span className="ml-2">La réception des travaux est prononcée sans réserves avec effet à la date du {formatFR(formData.dateReception)}</span>
                    </label>
                    <label>
                        <input type="radio" name="decision" value="réservée" checked={formData.decision === 'réservée'} onChange={handleChange} />
                        <span className="ml-2">La réception des travaux est prononcée avec les réserves consignées dans l’annexe ci-jointe à la date du {formatFR(formData.dateReception)}</span>
                    </label>
                    <label>
                        <input type="radio" name="decision" value="refusée" checked={formData.decision === 'refusée'} onChange={handleChange} />
                        <span className="ml-2">La réception des travaux est refusée pour les motifs consignés dans l’annexe ci-jointe à la date du {formatFR(formData.dateReception)}</span>
                    </label>
                </div>

                {(formData.decision === 'réservée' || formData.decision === 'refusée') && (
                    <p className="mt-4 text-sm">
                        L'entreprise et le Client conviennent que les travaux nécessités par les réserves ci-dessous seront exécutés
                        dans un délai de <input name="delai" value={formData.delai} onChange={handleChange} className="p-2 border rounded w-10" /> jours.
                    </p>
                )}

                {(formData.decision === 'réservée' || formData.decision === 'refusée') && (
                    // <div className="bg-white shadow-md mx-auto mt-12 px-20 py-24 w-full max-w-[1000px]">

                    <div className="mt-5">
                        <h2 className="mb-4 font-semibold text-lg">Listes des Réserves:</h2>
                        <div className="my-5 border-t"></div>

                        {reserves.map((res, i) => (
                            <div key={i} className="bg-white shadow-sm mb-6 p-4 border border-gray-300 rounded-md">
                                <div className="mb-3">
                                    <label className="block mb-1 font-medium text-sm">Réserve {i + 1}</label>
                                    <textarea
                                        value={res.text}
                                        placeholder={`Ex : Défaut d'étanchéité, fissure murale, etc.`}
                                        onChange={(e) => handleReserveChange(i, e.target.value)}
                                        className="p-2 border rounded focus:ring-2 focus:ring-blue-300 w-full text-sm resize-none"
                                        rows={3}
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="block mb-1 font-medium text-sm">Fichiers joints</label>
                                    <input
                                        type="file"
                                        accept="image/*,application/pdf"
                                        multiple
                                        onChange={(e) => handleFileUpload(i, e.target.files)}
                                        className="text-sm"
                                    />
                                </div>
                                {res.files?.length > 0 && (
                                    <div className="space-y-1 mt-2 text-gray-700 text-sm">
                                        {res.files.map((file, j) => (
                                            <div key={j} className="flex items-center gap-2">
                                                {file.type.includes('pdf') ? <span>📄</span> : <span>🖼️</span>}
                                                <span className="truncate">{file.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="mt-4 text-right">
                                    <button onClick={() => removeReserve(i)} className="text-red-500 hover:text-red-700 text-sm">
                                        Supprimer cette réserve
                                    </button>
                                </div>
                            </div>
                        ))}

                        <div className="text-right">
                            <button onClick={addReserve} className="bg-orange-600 hover:bg-orange-500 px-4 py-2 rounded text-white text-sm transition-all duration-200">
                                + Ajouter une réserve
                            </button>
                        </div>
                    </div>

                    // </div>
                )}

                <div className="mt-6 text-sm">
                    <p>
                        Fait à{' '}
                        <input name="ville" placeholder="Ville" value={formData.ville} onChange={handleChange} className="inline-block p-1 border-gray-400 border-b w-40" />
                        {' '}le{' '}
                        <input type="date" name="dateFait" value={formData.dateFait} onChange={handleChange} className="p-2 border rounded" />
                    </p>
                </div>

                <div className="flex justify-between gap-8 mt-8">
                    <div className="flex flex-col items-center">
                        <p className="mb-1 text-sm">Signature de l’entreprise</p>
                        <SignaturePad ref={signatureEntrepriseRef} canvasProps={{ className: 'border w-full h-24 w-[300px] rounded' }} />
                    </div>
                    <div className="flex flex-col items-center">
                        <p className="mb-1 text-sm">Signature du client</p>
                        <SignaturePad ref={signatureClientRef} canvasProps={{ className: 'border w-full h-24 w-[300px] rounded' }} />
                    </div>
                </div>
            </div>

        </div>
    );
});

export default QuitusForm;
